import { toMarkdown, toPlainText } from './conversationExportFormatting';
import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import slugify from '@/constants/utils/slugify';
import { chatsApi } from '@/lib/api';
export { toMarkdown, toPlainText } from './conversationExportFormatting';

/**
 * Trigger a file download on web via blob (string content)
 */
function downloadBlobWeb(content, filename, mimeType) {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Share a text file on native via the share sheet
 */
async function shareFileNative(content, filename, mimeType) {
  const fileUri = `${FileSystem.cacheDirectory}${filename}`;
  await FileSystem.writeAsStringAsync(fileUri, content, {
    encoding: FileSystem.EncodingType.UTF8,
  });

  const isAvailable = await Sharing.isAvailableAsync();
  if (!isAvailable) {
    throw new Error('Sharing is not available on this device');
  }

  await Sharing.shareAsync(fileUri, {
    mimeType,
    dialogTitle: 'Export Conversation',
  });

  setTimeout(async () => {
    try {
      await FileSystem.deleteAsync(fileUri, { idempotent: true });
    } catch (_) {
      // ignore cleanup
    }
  }, 60000);
}

/**
 * Convert a Blob to a base64 string. Used to write a binary file (e.g. a
 * PDF) to the native cache directory before handing it to the share sheet.
 */
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('Failed to read blob'));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        const commaIdx = result.indexOf(',');
        resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
      } else {
        reject(new Error('Unexpected reader result type'));
      }
    };
    reader.readAsDataURL(blob);
  });
}

/**
 * Share a binary blob (e.g. PDF bytes) on native via the share sheet.
 */
async function shareBinaryNative(blob, filename, mimeType) {
  const base64 = await blobToBase64(blob);
  const fileUri = `${FileSystem.cacheDirectory}${filename}`;
  await FileSystem.writeAsStringAsync(fileUri, base64, {
    encoding: FileSystem.EncodingType.Base64,
  });

  const isAvailable = await Sharing.isAvailableAsync();
  if (!isAvailable) {
    throw new Error('Sharing is not available on this device');
  }

  await Sharing.shareAsync(fileUri, {
    mimeType,
    dialogTitle: 'Export Conversation',
  });

  setTimeout(async () => {
    try {
      await FileSystem.deleteAsync(fileUri, { idempotent: true });
    } catch (_) {
      // ignore cleanup
    }
  }, 60000);
}

function exportExtension(format) {
  return format === 'pdf' ? 'pdf'
    : format === 'text' ? 'txt'
    : 'md';
}

/**
 * Pick a download filename. Prefers the server's Content-Disposition
 * (which keeps Unicode titles). slugify() strips every non-ASCII character,
 * so a CJK/emoji-only title used to become ".pdf" and iOS sharing would
 * refuse the file — the same chats the Unicode PDF renderer was meant to
 * fix.
 */
export function conversationExportFilename(chat, format, serverFilename) {
  const ext = exportExtension(format);
  if (serverFilename) {
    const base = String(serverFilename).split(/[/\\]/).pop() || '';
    const cleaned = base.replace(/[\0\r\n]/g, '').trim();
    if (cleaned && cleaned !== `.${ext}`) return cleaned;
  }
  const slug = slugify(chat?.title || 'conversation') || 'conversation';
  return `${slug}.${ext}`;
}

function blobFromExportResult(blobOrResult) {
  if (blobOrResult && typeof blobOrResult === 'object' && blobOrResult.blob) {
    return blobOrResult.blob;
  }
  return blobOrResult;
}

/**
 * Save a binary Blob produced by the server-side export (e.g. a rendered
 * PDF, or any format when the client falls back to the server). On web
 * this triggers a browser download; on native it writes the blob to the
 * cache directory and hands it to the OS share sheet.
 *
 * Kept separate from `exportConversation` so callers that already have a
 * blob (the no-local-messages fallback path, the PDF branch) can reuse it
 * without re-fetching.
 *
 * `blobOrResult` may be a Blob or `{ blob, filename }` from fetchWithAuth
 * with `responseType: 'blob'`.
 */
export async function saveExportedBlob(blobOrResult, chat, format) {
  const blob = blobFromExportResult(blobOrResult);
  const serverFilename = typeof blobOrResult?.filename === 'string' ? blobOrResult.filename : null;
  const filename = conversationExportFilename(chat, format, serverFilename);
  const mimeType = format === 'pdf' ? 'application/pdf'
    : format === 'text' ? 'text/plain'
    : 'text/markdown';

  if (Platform.OS === 'web') {
    downloadBlobWeb(blob, filename, mimeType);
  } else {
    await shareBinaryNative(blob, filename, mimeType);
  }
}

/**
 * Export a full conversation in the given format.
 *
 * Markdown / text exports are built CLIENT-SIDE from the in-memory chat
 * object (so the export reflects exactly what the user sees, including
 * any local edits / pending state). PDF exports are built SERVER-SIDE so
 * the heavy markdown -> PDF rendering doesn't block the UI thread.
 *
 * On web: triggers a browser download.
 * On native: opens the OS share sheet.
 */
export async function exportConversation(chat, { format = 'markdown', getModelById } = {}) {
  const opts = { getModelById };
  const baseFilename = slugify(chat.title || 'conversation') || 'conversation';

  // PDF -> server-side render
  if (format === 'pdf') {
    if (!chat?.id) {
      throw new Error('Cannot export PDF: conversation id missing');
    }
    const result = await chatsApi.downloadConversationExport(chat.id, 'pdf');
    await saveExportedBlob(result, chat, 'pdf');
    return;
  }

  // Markdown / text -> client-side build
  let content, filename, mimeType;
  if (format === 'text') {
    content = toPlainText(chat, opts);
    filename = `${baseFilename}.txt`;
    mimeType = 'text/plain';
  } else {
    content = toMarkdown(chat, opts);
    filename = `${baseFilename}.md`;
    mimeType = 'text/markdown';
  }

  if (Platform.OS === 'web') {
    downloadBlobWeb(content, filename, mimeType);
  } else {
    await shareFileNative(content, filename, mimeType);
  }
}

export default exportConversation;
